import { useLayoutEffect, useRef, useState } from 'react'
import { createWebSharedValue, processAnimatedStyleObject, runWithTracking } from './animations'
import type { WebSharedValue } from './types'

export function useSharedValue<T>(initialValue: T): WebSharedValue<T> {
  const sharedValueRef = useRef<WebSharedValue<T> | null>(null)

  if (!sharedValueRef.current) {
    sharedValueRef.current = createWebSharedValue(initialValue)
  }

  return sharedValueRef.current
}

export function useDerivedValue<T>(factory: () => T): WebSharedValue<T> {
  const sharedValue = useSharedValue(factory())
  const factoryRef = useRef(factory)
  factoryRef.current = factory

  useLayoutEffect(() => {
    sharedValue.set(factoryRef.current() as T & import('./types').AnimationConfig)
  })

  return sharedValue
}

export function useAnimatedStyle<T extends Record<string, unknown>>(factory: () => T, dependencies: unknown[] = []): T {
  const ownerRef = useRef({})
  const factoryRef = useRef(factory)
  factoryRef.current = factory
  const [style, setStyle] = useState<T>(() => {
    const { result } = runWithTracking(() => factoryRef.current())

    return processAnimatedStyleObject(ownerRef.current, result, () => undefined) as T
  })

  useLayoutEffect(() => {
    // Worklet может читать shared values условно (напр. только активная точка пагинации читает
    // progress), поэтому набор зависимостей меняется между запусками. Держим живую карту подписок
    // и переподписываемся динамически после каждого прогона, а не один раз по первому запуску —
    // иначе значения, прочитанные впервые позже, никогда не будут обновлять стиль.
    const subscriptions = new Map<WebSharedValue<unknown>, () => void>()
    let disposed = false

    const updateStyle = () => {
      const { result, sharedValues } = runWithTracking(() => factoryRef.current())

      for (const sharedValue of sharedValues) {
        if (!subscriptions.has(sharedValue)) {
          subscriptions.set(sharedValue, sharedValue._subscribe(updateStyle))
        }
      }

      for (const [sharedValue, unsubscribe] of subscriptions) {
        if (!sharedValues.has(sharedValue)) {
          unsubscribe()
          subscriptions.delete(sharedValue)
        }
      }

      if (!disposed) {
        setStyle(processAnimatedStyleObject(ownerRef.current, result, updateStyle) as T)
      }
    }

    updateStyle()

    return () => {
      disposed = true
      subscriptions.forEach((unsubscribe) => {
        unsubscribe()
      })
      subscriptions.clear()
    }
  }, dependencies)

  return style
}
